import { Document, DocumentType } from '@talon/web/lib/api';
import { useProjectStore } from '@talon/web/src/context';
import { Box, CheckBox, FileInput, Spinner, Text } from 'grommet';
import {
  Certificate,
  CircleQuestion,
  DocumentPerformance,
  DocumentText,
  DocumentTime,
  DocumentUpload,
  DocumentVideo,
} from 'grommet-icons';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ModalCard } from '../ModalCard';
import { Selector } from '../Selector';
import { TalonTip } from '../TalonTip';

type IDocConfig = {
  [key in DocumentType]: {
    icon: JSX.Element;
    acceptable: string;
    allowsAi?: boolean;
  };
};

const EXCEL = '.xls,.xlsx,.csv';
const WORD = '.docx';
const PDF = '.pdf';
// const PPT = '.ppt,.pptx';

export const documentConfig: IDocConfig = {
  [DocumentType.Script]: {
    icon: <DocumentText />,
    acceptable: [WORD, PDF].join(','),
    allowsAi: true,
  },
  [DocumentType.MediaPlan]: {
    icon: <DocumentPerformance />,
    acceptable: EXCEL,
    allowsAi: false,
  },
  [DocumentType.ShootingSchedule]: {
    icon: <DocumentTime />,
    acceptable: EXCEL,
    allowsAi: false,
  },
  [DocumentType.Storyboard]: {
    icon: <DocumentVideo />,
    acceptable: PDF,
    allowsAi: false,
  },
  [DocumentType.License]: {
    icon: <Certificate />,
    acceptable: [WORD, PDF].join(','),
    allowsAi: true,
  },
  [DocumentType.Contract]: {
    icon: <DocumentText />,
    acceptable: [WORD, PDF].join(','),
    allowsAi: false,
  },
  [DocumentType.Generic]: {
    icon: <DocumentText />,
    acceptable: [WORD, PDF, EXCEL].join(','),
    allowsAi: false,
  },
};

interface IDocumentUploadModal {
  documentTypes?: DocumentType[];
  setShowUpload: (show: boolean) => void;
  successCallback?: (id?: Document) => void;
  allowAi?: boolean;
}

export const DocumentUploadModal = ({
  documentTypes,
  setShowUpload,
  successCallback,
  allowAi,
}: IDocumentUploadModal) => {
  /** Context **/
  const projectStore = useProjectStore();

  /** State **/
  const [loading, setLoading] = useState(false);
  const [acceptable, setAcceptable] = useState('');
  const [fileType, setFileType] = useState<DocumentType | ''>('');
  const [useAi, setUseAi] = useState(allowAi || false);

  /** Methods **/

  const chooseUploadFileType = (type: DocumentType) => {
    setAcceptable(documentConfig[type].acceptable);
    setFileType(type);
  };

  const doUpload = async (e?: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (!files?.[0] || !fileType) return;

    setLoading(true);
    const doc = await projectStore.createDocument(files[0], fileType, useAi ? 'true' : 'false');
    if (!doc) {
      toast.error('Error uploading document');
    }
    setLoading(false);
    setShowUpload(false);
    successCallback?.(doc);
  };

  /** Effects **/

  useEffect(() => {
    if (documentTypes?.length === 1) {
      setFileType(documentTypes[0]);
      setAcceptable(documentConfig[documentTypes[0]].acceptable);
    }
  }, [documentTypes]);

  /** Render **/

  return (
    <ModalCard onClose={() => setShowUpload(false)} title="Upload Document" icon={<DocumentUpload />}>
      {!loading && (
        <Box>
          {documentTypes?.length === 1 ? (
            <Text>Upload a {documentTypes[0]} Document</Text>
          ) : (
            <Selector
              options={Object.keys(documentConfig)
                .filter((key) => (documentTypes ? documentTypes.includes(key as DocumentType) : true))
                .map((key) => ({ name: key, id: key }))}
              onChange={(value) => chooseUploadFileType(value as DocumentType)}
              placeholder="Choose document type..."
              size="medium"
              clear={true}
            />
          )}
          {fileType && documentConfig[fileType]?.allowsAi && allowAi && (
            <Box direction="row" align="center" gap="small" margin={{ top: 'small', bottom: 'small' }}>
              <CheckBox label="Use AI" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />
              <TalonTip
                content={
                  <Text>Use AI to extract information from document and automatically populate data in Talon</Text>
                }
              >
                <Box>
                  <CircleQuestion color="brand" />
                </Box>
              </TalonTip>
            </Box>
          )}
          <FileInput
            name="file"
            onChange={(e) => doUpload(e)}
            accept={acceptable}
            disabled={!acceptable}
            messages={{
              dropPrompt: 'Drop document here or click to browse',
              browse: 'Browse',
            }}
          />
          <Text size="xsmall">
            {acceptable && 'Acceptable file types: '}
            {acceptable}
          </Text>
        </Box>
      )}
      {loading && <Spinner size="large" background="light-1" alignSelf="center" margin="small" />}
    </ModalCard>
  );
};
